import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { IAppointmentView, IBlockerView, appointmentService, blockerService } from '../../../../services';
import { SchedulerFilterOptionsEnum } from '../Scheduler';
import { Event } from './Event';
import { BlockerEvent } from './BlockerEvent';
import { computeColumnLayout } from '../utils/overlapLayout';
import { CurrentTimeIndicator } from './CurrentTimeIndicator';

const HOUR_HEIGHT = 100;
const HEADER_HEIGHT = 60;

interface IProps {
  businessId: number | undefined;
  filterOptionType: number;
  option: { id: number, name: string } | undefined;
  date: Date;
  startTime: number;
  endTime: number;
  hours: string[];
  refetch: () => any;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const WeekView: React.FC<IProps> = ({
  businessId,
  filterOptionType,
  option,
  date,
  startTime,
  endTime,
  hours,
  refetch,
  setIsLoading
}) => {
  const theme = useTheme();

  const daysOfWeek = [
    { id: 0, name: 'DOM' },
    { id: 1, name: 'SEG' },
    { id: 2, name: 'TER' },
    { id: 3, name: 'QUA' },
    { id: 4, name: 'QUI' },
    { id: 5, name: 'SEX' },
    { id: 6, name: 'SÁB' },
  ];

  // Fix #2 — memoize weekDays so it's computed once per date change, not on every render
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => {
      const d = new Date(date);
      d.setDate(d.getDate() - d.getDay() + index);
      return d;
    });
  }, [date]);

  const { data: appointments, isFetching: isFetchingAppointments, refetch: refetchAppointments } = useQuery(
    ['appointments', businessId, option, date],
    () => appointmentService.get(
      filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId || undefined,
      filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined,
      weekDays[0],
      weekDays[6],
      true),
    { enabled: !!date && (!!businessId || !!option) }
  );

  const { data: blockers, isFetching: isFetchingBlockers, refetch: refetchBlockers } = useQuery(
    ['blockers', businessId, option, date],
    () => blockerService.get(
      filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId || undefined,
      filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined,
      weekDays[0],
      weekDays[6]),
    { enabled: !!date && (!!businessId || !!option) }
  );

  // Fix #3 — correct dependency array
  useEffect(() => {
    setIsLoading(isFetchingAppointments || isFetchingBlockers);
  }, [isFetchingAppointments, isFetchingBlockers]);

  const sortedAppointments = useMemo(
    () => [...(appointments || [])].sort((a: IAppointmentView, b: IAppointmentView) => (a.date < b.date ? -1 : 1)),
    [appointments]
  );

  const sortedBlockers = useMemo(
    () => [...(blockers || [])].sort((a: IBlockerView, b: IBlockerView) => (a.startTime < b.startTime ? -1 : 1)),
    [blockers]
  );

  // Fix #4 — timezone-safe grouping: compare local date strings instead of UTC getDay()
  const appointmentsByDayOfWeek = useMemo((): IAppointmentView[][] => {
    const separated: IAppointmentView[][] = Array.from({ length: 7 }, () => []);
    sortedAppointments.forEach((appointment) => {
      const appointmentDateStr = new Date(appointment.date).toDateString();
      weekDays.forEach((weekDay, index) => {
        if (appointmentDateStr === weekDay.toDateString()) {
          separated[index].push(appointment);
        }
      });
    });
    return separated;
  }, [sortedAppointments, weekDays]);

  const blockersByDayOfWeek = useMemo((): IBlockerView[][] => {
    const separated: IBlockerView[][] = Array.from({ length: 7 }, () => []);
    sortedBlockers.forEach((blocker) => {
      // Safely extract YYYY-MM-DD for start
      let startStr = '0000-01-01';
      if (blocker.startDate) {
        const parts = String(blocker.startDate).split('T')[0];
        if (parts.startsWith('20')) startStr = parts;
      }

      // Safely extract YYYY-MM-DD for end
      let endStr = '9999-12-31';
      if (blocker.endDate) {
        const parts = String(blocker.endDate).split('T')[0];
        if (parts.startsWith('20')) endStr = parts;
      }

      weekDays.forEach((currentDay, index) => {
        // Format currentDay as YYYY-MM-DD
        const month = String(currentDay.getMonth() + 1).padStart(2, '0');
        const day = String(currentDay.getDate()).padStart(2, '0');
        const currentStr = `${currentDay.getFullYear()}-${month}-${day}`;

        // String comparison is completely timezone safe!
        if (currentStr >= startStr && currentStr <= endStr) {
          if (!blocker.daysOfWeek || blocker.daysOfWeek.length === 0 || blocker.daysOfWeek.includes(currentDay.getDay())) {
            separated[index].push(blocker);
          }
        }
      });
    });
    return separated;
  }, [sortedBlockers, weekDays]);

  // Fix #5 + #9 — unified layout: appointments and blockers share columns per day
  const columnLayoutByDay = useMemo(() => {
    return weekDays.map((_, index) => {
      const allEvents = [
        ...appointmentsByDayOfWeek[index].map(a => ({ id: `app-${a.id}`, startTime: a.startTime, endTime: a.endTime })),
        ...blockersByDayOfWeek[index].map(b => ({ id: `blk-${b.id}`, startTime: b.startTime, endTime: b.endTime })),
      ];
      return computeColumnLayout(allEvents);
    });
  }, [appointmentsByDayOfWeek, blockersByDayOfWeek]);

  return (
    <Box sx={{ width: '100%', display: 'flex', pb: 2, overflow: 'hidden' }}>

      {/* Sticky hours column */}
      <Box
        display='flex'
        flexDirection='column'
        alignItems='flex-start'
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 2,
          backgroundColor: 'background.paper',
          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
        }}
      >
        {/* Spacer aligned with day header row + divider */}
        <Box style={{ width: '72px', height: HEADER_HEIGHT + 17 }} />

        {hours.map((time) => (
          <Box
            key={time}
            display='flex'
            justifyContent='flex-end'
            alignItems='flex-start'
            gap={1}
            style={{ width: '72px', height: HOUR_HEIGHT / 2 }}
          >
            {/* Fix #8 — offset label up to align with grid line */}
            <Typography
              variant='caption'
              sx={{
                mt: '-9px',
                lineHeight: 1,
                userSelect: 'none',
                opacity: time.endsWith(':00') ? 0.8 : 0.35,
                fontSize: time.endsWith(':00') ? '0.72rem' : '0.62rem',
              }}
            >
              {time}
            </Typography>
            <Divider orientation='vertical' flexItem />
          </Box>
        ))}
      </Box>

      {/* Day columns */}
      <Box display='flex' sx={{ flex: 1, minWidth: 0 }}>
        {weekDays.map((day, index) => (
          <Box key={index} flex={1} sx={{ minWidth: 100, borderRight: index < 6 ? '1px solid' : 'none', borderColor: 'divider' }}>

            {/* Day header */}
            <Box
              height={HEADER_HEIGHT}
              width={HEADER_HEIGHT}
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
              m='auto'
              borderRadius='50%'
              sx={{
                backgroundColor: day.toDateString() === new Date().toDateString() ? theme.palette.primary.main : 'transparent',
                color: day.toDateString() === new Date().toDateString() ? theme.palette.primary.contrastText : 'inherit',
                boxShadow: day.toDateString() === new Date().toDateString() ? '0px 4px 10px rgba(0,0,0,0.2)' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              <Typography variant='h6'>{day.getDate()}</Typography>
              <Typography variant='caption' m={0}>{daysOfWeek[index].name}</Typography>
            </Box>
            <Divider sx={{ mb: 1, mt: 1 }} />

            {/* Event area */}
            <Box width='100%' position='relative'>

              {/* Fix #7 — horizontal grid lines */}
              {hours.map((time, i) => (
                <Box
                  key={`grid-${time}`}
                  sx={{
                    position: 'absolute',
                    top: `${i * (HOUR_HEIGHT / 2)}px`,
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: time.endsWith(':00') ? 'divider' : 'rgba(128,128,128,0.12)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {/* Fix #6 — current time indicator (only on today's column) */}
              {day.toDateString() === new Date().toDateString() && (
                <CurrentTimeIndicator startTime={startTime} endTime={endTime} hourHeight={HOUR_HEIGHT} />
              )}

              {appointmentsByDayOfWeek[index]
                .sort((a, b) => (a.date < b.date ? -1 : 1))
                .map((appointment: IAppointmentView) => (
                  <Event
                    key={appointment.id}
                    firstTime={startTime}
                    currentEvent={appointment}
                    columnIndex={columnLayoutByDay[index]?.get(`app-${appointment.id}`)?.columnIndex ?? 0}
                    totalColumns={columnLayoutByDay[index]?.get(`app-${appointment.id}`)?.totalColumns ?? 1}
                    hourHeight={HOUR_HEIGHT}
                    maxLengthDescription={50}
                    refetch={refetch}
                    refetchAppointments={refetchAppointments}
                  />
                ))}

              {blockersByDayOfWeek[index].map((blocker: IBlockerView) => (
                <BlockerEvent
                  key={`blocker-${blocker.id}`}
                  currentEvent={blocker}
                  firstTime={startTime}
                  hourHeight={HOUR_HEIGHT}
                  maxLengthDescription={100}
                  columnIndex={columnLayoutByDay[index]?.get(`blk-${blocker.id}`)?.columnIndex ?? 0}
                  totalColumns={columnLayoutByDay[index]?.get(`blk-${blocker.id}`)?.totalColumns ?? 1}
                  refetch={refetch}
                  refetchBlockers={refetchBlockers}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
